import { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Globe2, Send, Loader2, Check } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useToast } from '../hooks/use-toast';
import useSEO from '../hooks/useSEO';
import useJsonLd from '../hooks/useJsonLd';
import { PAGE_SEO, SITE_CONFIG } from '../data/seoConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { name: '', email: '', phone: '', company: '', website: '', country: '', city: '', service: '', message: '' };

export default function Contact() {
  useSEO(PAGE_SEO.contact);

  useJsonLd('contact-schema', [
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      url: `${SITE_CONFIG.url}/contact`,
      name: 'Contact Ransen',
      description: PAGE_SEO.contact.description,
      mainEntity: { '@id': `${SITE_CONFIG.url}/#organization` },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_CONFIG.url },
        { '@type': 'ListItem', position: 2, name: 'Contact', item: `${SITE_CONFIG.url}/contact` },
      ],
    },
  ]);

  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast({ title: 'Message sent', description: 'A senior strategist will reach out within one business day.' });
      setForm(EMPTY_FORM);
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again or email marketing@ransen.ca', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow='Contact'
        title={<><span className='text-white'>Let’s build</span> <span className='text-brand-gradient'>your growth system</span></>}
        description='Tell us about your goals. A senior strategist will respond within one business day.'
        crumbs={[{ label: 'Contact' }]}
      />

      <section className='py-16'>
        <div className='max-w-6xl mx-auto px-6 grid lg:grid-cols-3 gap-10'>
          <div className='lg:col-span-1 space-y-5'>
            <div className='glass-card rounded-2xl p-6' data-testid='contact-email-card'>
              <div className='w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/10 border border-violet-500/25 flex items-center justify-center text-violet-200'><Mail className='w-4 h-4'/></div>
              <div className='mt-4 text-white font-semibold'>Email</div>
              <a href='mailto:marketing@ransen.ca' className='text-white/60 hover:text-white text-sm break-all' data-testid='contact-email'>marketing@ransen.ca</a>
            </div>
            <div className='glass-card rounded-2xl p-6' data-testid='contact-phone-card'>
              <div className='w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/10 border border-violet-500/25 flex items-center justify-center text-violet-200'><Phone className='w-4 h-4'/></div>
              <div className='mt-4 text-white font-semibold'>Phone</div>
              <a href='tel:+13435732394' className='text-white/60 hover:text-white text-sm' data-testid='contact-phone'>+1 (343) 573-2394</a>
            </div>
            <div className='glass-card rounded-2xl p-6'>
              <div className='w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/10 border border-violet-500/25 flex items-center justify-center text-violet-200'><Globe2 className='w-4 h-4'/></div>
              <div className='mt-4 text-white font-semibold'>Global coverage</div>
              <div className='text-white/60 text-sm mt-1'>North America · Europe · Middle East · APAC</div>
            </div>
            <div className='glass-card rounded-2xl p-6'>
              <div className='w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/10 border border-violet-500/25 flex items-center justify-center text-violet-200'><MapPin className='w-4 h-4'/></div>
              <div className='mt-4 text-white font-semibold'>Hubs</div>
              <div className='text-white/60 text-sm mt-1'>Toronto · London · Dubai · Singapore</div>
            </div>
          </div>

          <form onSubmit={onSubmit} className='lg:col-span-2 glass-card rounded-2xl p-7 md:p-9' data-testid='contact-form'>
            <div className='grid sm:grid-cols-2 gap-5'>
              <Field label='Name *'>
                <input required name='name' value={form.name} onChange={onChange} className='input-base' data-testid='form-name'/>
              </Field>
              <Field label='Work email *'>
                <input required type='email' name='email' value={form.email} onChange={onChange} className='input-base' data-testid='form-email'/>
              </Field>
              <Field label='Phone (optional)'>
                <input type='tel' name='phone' value={form.phone} onChange={onChange} className='input-base' placeholder='+1 555 123 4567' data-testid='form-phone'/>
              </Field>
              <Field label='Company (optional)'>
                <input name='company' value={form.company} onChange={onChange} className='input-base' data-testid='form-company'/>
              </Field>
              <Field label='Website (optional)'>
                <input name='website' value={form.website} onChange={onChange} className='input-base' placeholder='yourcompany.com' data-testid='form-website'/>
              </Field>
              <Field label='Country *'>
                <input required name='country' value={form.country} onChange={onChange} className='input-base' placeholder='e.g. Canada' data-testid='form-country'/>
              </Field>
              <Field label='City *'>
                <input required name='city' value={form.city} onChange={onChange} className='input-base' placeholder='e.g. Toronto' data-testid='form-city'/>
              </Field>
              <Field label='Interested in (optional)'>
                <select name='service' value={form.service} onChange={onChange} className='input-base' data-testid='form-service'>
                  <option value=''>Select a service</option>
                  {['Paid Media Advertising','Search Engine Optimization (SEO)','Social Media Management','Website Design & Development','Video Production','AI Automation','LLMO Optimization','AEO Optimization','Business Operations Support','Full Growth Program','Not sure yet'].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>

            <Field label='How can we help? *'>
              <textarea required name='message' rows={5} value={form.message} onChange={onChange} className='input-base resize-none' placeholder='Tell us a bit about your business and what you’d like to achieve...' data-testid='form-message' />
            </Field>

            <div className='mt-6 flex flex-col sm:flex-row items-center justify-between gap-4'>
              <div className='text-white/45 text-[12.5px]'>By submitting, you agree to our privacy policy.</div>
              <button type='submit' disabled={loading} className='btn-primary text-white text-sm font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2 disabled:opacity-70' data-testid='form-submit'>
                {loading ? <><Loader2 className='w-4 h-4 animate-spin'/> Sending...</> : done ? <><Check className='w-4 h-4'/> Sent</> : <>Send Message <Send className='w-4 h-4'/></>}
              </button>
            </div>
          </form>
        </div>
      </section>

      <style>{`
        .input-base {
          width: 100%;
          padding: 12px 14px;
          border-radius: 12px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.09);
          color: #fff;
          font-size: 14.5px;
          outline: none;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .input-base:focus {
          border-color: rgba(168, 85, 247, 0.55);
          background: rgba(255,255,255,0.06);
        }
        .input-base::placeholder { color: rgba(255,255,255,0.35); }
        select.input-base option { background: #0a061a; color: #fff; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className='block mt-5 first:mt-0'>
      <div className='text-white/70 text-[12.5px] font-medium mb-2 tracking-wide'>{label}</div>
      {children}
    </label>
  );
}
